import React, { useState } from "react";

import {
  useProjectStatusesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveStrategicAxes,
  useRetrieveZonesIndicators,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { Sort, Filter } from "@planzone/dto";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { Safeguard } from "@scenes/safeguard";
import { usePreferences } from "@components/preferences";
import { useModal } from "@root/client/modal";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { InfiniteScroll, Flag } from "@common/infiniteScroll";
import { AddProjectButton } from "./addProjectButton";
import { Toolbar } from "./toolbar";
import { Loader } from "./loader";
import { Zone } from "./zone";
import { ExportButton } from "./exportButton";
import { Props, SortOptions } from "./types";

import styles from "./styles.scss";

function ProjectsPage(props: Props) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const [sort, setSort] = useState<SortOptions>(SortOptions.Default);
  const m: { [Key in SortOptions]: Sort } = {
    [SortOptions.Default]: "ascendingName",
    [SortOptions.AscendingAlphabeticalOrder]: "ascendingName",
    [SortOptions.DescendingAlphabeticalOrder]: "descendingName",
    [SortOptions.StartDate]: "ascendingStartDate",
    [SortOptions.EndDate]: "descendingEndDate",
    [SortOptions.CreationDate]: "ascendingCreationDate",
  };
  const s: Sort = m[sort];
  const {
    isRequestPending: isStrategicAxesRequestPending,
    isRequestSuccess: isStrategicAxesRequestSuccess,
    isRequestFailure: isStrategicAxesRequestFailure,
    retrieveStrategicAxes,
  } = useRetrieveStrategicAxes();
  const { state, edit } = usePreferences();
  let { zonesProjectsFilter: filter, projectListingColumnSizes } = state;
  const { statuses } = useProjectStatusesRetrieval();

  if (!filter.projectStatuses || filter.projectStatuses.length === 0) {
    filter = {
      ...filter,
      projectStatuses: statuses.filter(
        (status) => status.label !== "pz_closed",
      ),
    };
  }
  const {
    zones,
    index,
    canRetrieveNextZonesIndicators,
    isRequestPending,
    retrieveZonesIndicators,
  } = useRetrieveZonesIndicators(filter, s, 10, "indicators");

  const setFilter = (nextFilter: Filter) =>
    edit({ ...state, zonesProjectsFilter: nextFilter });
  const { pushView } = useModal();
  const [columnSizes, setColumnSizes] = React.useState(
    projectListingColumnSizes,
  );

  React.useEffect(() => {
    !isStrategicAxesRequestPending &&
      !isStrategicAxesRequestSuccess &&
      !isStrategicAxesRequestFailure &&
      retrieveStrategicAxes(planzone);
  }, []);

  useMessage(
    ({ type }) =>
      (type === events.assignProjectToPortfolioSucceeded ||
        type === events.createProjectStrategicAxisSucceeded ||
        type === events.editProjectStrategicAxisSucceeded) &&
      retrieveZonesIndicators({
        sort: s,
        filter,
        size: 10,
        index: 0,
        view: "indicators",
      }),
  );

  return (
    <div className={styles.projectsPage}>
      <div className={styles.header}>
        <Toolbar
          sort={sort}
          filter={filter}
          setSort={setSort}
          setFilter={setFilter}
        />
        <Safeguard
          useCases={[
            UseCases.createProject,
            UseCases.editProjectListingConfiguration,
          ]}
        >
          {({ canCreateProject, canEditProjectListingConfiguration }) => (
            <div className={styles.row}>
              {canEditProjectListingConfiguration && (
                <div
                  className={styles.contextualMenuIcon}
                  onClick={() =>
                    pushView({
                      key: UseCases.retrieveProjectListingConfiguration,
                      data: undefined,
                    })
                  }
                >
                  <CogWheelIcon className={styles.wbsConfigurationIcon} />
                </div>
              )}
              {canCreateProject && (
                <div
                  className={styles.addZoneButton}
                  onClick={() =>
                    planzone.uuid === "57ec7e80-dcbb-49af-b11f-665fa6e545c1"
                      ? pushView({
                          key: UseCases.useProjectModel,
                          data: {},
                        })
                      : pushView({
                          key: UseCases.createProject,
                          data: { isMultiProject: true },
                        })
                  }
                >
                  <AddProjectButton />
                </div>
              )}
            </div>
          )}
        </Safeguard>
      </div>
      <InfiniteScroll className={styles.content} canFetchOnIdle>
        {() => (
          <div className={styles.main}>
            <ul className={styles.zones}>
              {[...zones].reverse().map((zone) => (
                <li key={zone.uuid} className={styles.zone}>
                  <Zone
                    zone={zone}
                    columnSizes={columnSizes}
                    sort={s}
                    filter={filter}
                    resizeCol={(key, size) =>
                      setColumnSizes((sizes) => ({
                        ...sizes,
                        [key]: size,
                      }))
                    }
                    resizeDone={() =>
                      edit({
                        ...state,
                        projectListingColumnSizes: columnSizes,
                      })
                    }
                  />
                </li>
              ))}
            </ul>
            {isRequestPending && <Loader />}
            <Flag
              name="zonesFlag"
              retrieveItems={() =>
                canRetrieveNextZonesIndicators &&
                !isRequestPending &&
                retrieveZonesIndicators({
                  sort: s,
                  filter,
                  size: 10,
                  index,
                  view: "indicators",
                })
              }
              isDisabled={!canRetrieveNextZonesIndicators || isRequestPending}
            />
          </div>
        )}
      </InfiniteScroll>
      <div className={styles.exportButton}>
        <ExportButton zones={zones} sort={s} filter={filter} />
      </div>
    </div>
  );
}

export { ProjectsPage };
